import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { handleEnterNavKeyDown } from '../../lib/focusEnterNav';
import { XIcon } from './Icons';

interface NumberInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size' | 'onChange' | 'prefix' | 'suffix'> {
  value?: number | string;
  onChange?: (event: { target: { name?: string; value: number | null; type: string } }) => void;
  name?: string;
  placeholder?: string;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  showClearButton?: boolean;
  size?: 'sm' | 'default' | 'lg';
  variant?: 'default' | 'filled' | 'outlined' | 'underlined';
  error?: string;
  helperText?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  allowDecimals?: boolean;
  /** When true, Enter moves focus to the next field marked with data-enter-nav. */
  enterNav?: boolean;
}

const NumberInput: React.FC<NumberInputProps> = ({
  value,
  onChange,
  name,
  placeholder = 'Enter number',
  required = false,
  readOnly = false,
  disabled = false,
  className,
  min,
  max,
  step = 1,
  showClearButton = true,
  size = 'default',
  variant = 'default',
  error,
  helperText,
  prefix,
  suffix,
  allowDecimals = true,
  enterNav = false,
  onKeyDown,
  ...props
}) => {
  const { colors, theme } = useTheme();
  const [inputValue, setInputValue] = useState(value !== undefined && value !== null ? String(value) : '');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    // Allow empty string, numbers, and decimal point
    if (newValue === '' || newValue === '-') {
      setInputValue(newValue);
      if (onChange) {
        onChange({
          target: {
            name,
            value: null,
            type: 'number',
          },
        });
      }
      return;
    }

    // Validate number format
    const numberRegex = allowDecimals ? /^-?\d*\.?\d*$/ : /^-?\d*$/;
    if (numberRegex.test(newValue)) {
      setInputValue(newValue);
      const numValue = newValue === '' || newValue === '-' ? null : parseFloat(newValue);
      if (onChange) {
        onChange({
          target: {
            name,
            value: numValue,
            type: 'number',
          },
        });
      }
    }
  };

  // Handle clear button
  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setInputValue('');

    if (onChange) {
      onChange({
        target: {
          name,
          value: null,
          type: 'number',
        },
      });
    }

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle focus and blur
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    if (props.onFocus) {
      props.onFocus(event);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    // Validate min/max on blur
    if (inputValue && inputValue !== '-') {
      const numValue = parseFloat(inputValue);
      if (min !== undefined && numValue < min) {
        setInputValue(String(min));
        if (onChange) {
          onChange({
            target: {
              name,
              value: min,
              type: 'number',
            },
          });
        }
      } else if (max !== undefined && numValue > max) {
        setInputValue(String(max));
        if (onChange) {
          onChange({
            target: {
              name,
              value: max,
              type: 'number',
            },
          });
        }
      }
    }
    if (props.onBlur) {
      props.onBlur(event);
    }
  };

  // Update input value when prop value changes
  useEffect(() => {
    if (value !== undefined && value !== null) {
      setInputValue(String(value));
    } else {
      setInputValue('');
    }
  }, [value]);

  // Size variants
  const sizeClasses = {
    sm: 'px-2 py-1 text-sm',
    default: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base',
  };

  // Variant styles
  const variantClasses = {
    default: `${colors.surface} ${colors.text} ${colors.border}`,
    filled: `${colors.surfaceHover} ${colors.text} border-transparent`,
    outlined: `bg-transparent ${colors.text} ${colors.border}`,
    underlined: `bg-transparent ${colors.text} border-0 border-b-2 ${colors.border} rounded-none`,
  };

  const hasValue = inputValue !== '' && inputValue !== '-';

  return (
    <div className="relative" data-enter-nav={enterNav ? '' : undefined}>
      {/* Main input container */}
      <div className="relative">
        {/* Prefix */}
        {prefix && (
          <div className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${colors.textMuted} pointer-events-none z-10`}>
            {prefix}
          </div>
        )}

        {/* Input field */}
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          name={name}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            onKeyDown?.(e);
            if (!e.defaultPrevented && enterNav) {
              handleEnterNavKeyDown(e);
            }
          }}
          placeholder={placeholder}
          required={required}
          readOnly={readOnly}
          disabled={disabled}
          className={cn(
            'w-full min-h-10 border rounded-md transition-colors',
            sizeClasses[size],
            variantClasses[variant],
            prefix ? 'pl-10' : '',
            suffix ? 'pr-10' : '',
            showClearButton && hasValue && !disabled && !readOnly && 'pr-10',
            isFocused && cn(colors.border, 'ring-2 ring-offset-0', colors.primaryBorderAll ? colors.primaryBorderAll.replace('border-', 'ring-') : ''),
            error && 'border-red-500 focus:border-red-500 focus:ring-red-500/20',
            disabled && 'opacity-50 cursor-not-allowed',
            readOnly && `cursor-default ${colors.surfaceHover}`,
            className
          )}
          {...props}
        />

        {/* Suffix */}
        {suffix && (
          <div className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${colors.textMuted} pointer-events-none`}>
            {suffix}
          </div>
        )}

        {/* Clear button */}
        {showClearButton && hasValue && !disabled && !readOnly && (
          <button
            type="button"
            onClick={handleClear}
            className={cn('absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded transition-colors', colors.interactiveHover)}
            title="Clear"
          >
            <XIcon className={`w-4 h-4 ${colors.textMuted}`} />
          </button>
        )}
      </div>

      {/* Helper text and error */}
      {(error || helperText) && (
        <div className="mt-1">
          {error && <div className={cn('text-sm', colors.error)}>{error}</div>}
          {!error && helperText && <div className={`text-sm ${colors.textMuted}`}>{helperText}</div>}
        </div>
      )}
    </div>
  );
};

export default NumberInput;

